import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, SkipForward, SkipBack, RotateCcw, X } from "lucide-react";

/**
 * OnboardingVideo — "Vidéo animée" 100% HTML/CSS/SVG expliquant en 6 scènes
 * comment fonctionne Renforteo pour un nouveau candidat auto-entrepreneur.
 * Lecture auto (5s / scène), contrôles play/pause/prev/next/replay + fermeture.
 */

const SCENES = [
    {
        key: "welcome",
        icon: "👋",
        title: "Bienvenue sur Renforteo",
        subtitle: "La plateforme n°1 du renfort pro en France",
        body: "Missions ponctuelles, événementiel, restauration, sécurité, logistique — postulez à des missions près de chez vous, payées à l'heure, en toute simplicité.",
        color: "#0B192C",
    },
    {
        key: "signup",
        icon: "📝",
        title: "1. Inscrivez-vous en 3 minutes",
        subtitle: "Créez votre compte auto-entrepreneur",
        body: "Renseignez votre SIRET (obligatoire) et téléversez vos justificatifs : KBIS, carte d'identité et justificatif de domicile. Pas encore d'auto-entreprise ? On vous accompagne gratuitement.",
        color: "#F5A623",
    },
    {
        key: "validate",
        icon: "✅",
        title: "2. Notre équipe valide votre profil",
        subtitle: "Sous 24 à 48 heures",
        body: "Nous vérifions vos documents. Dès la validation, vous recevez un e-mail et vous pouvez postuler aux missions ouvertes. Aucun frais d'inscription, jamais.",
        color: "#10b981",
    },
    {
        key: "apply",
        icon: "🎯",
        title: "3. Postulez aux missions près de chez vous",
        subtitle: "Géolocalisées + filtrées à votre métier",
        body: "Recevez uniquement les missions qui correspondent à votre profil : métier, disponibilités et rayon de mobilité. Un clic suffit pour postuler.",
        color: "#0B192C",
    },
    {
        key: "work",
        icon: "💼",
        title: "4. Réalisez la mission, on gère le reste",
        subtitle: "Pointage, contrat, facturation — automatiques",
        body: "Signez le contrat de prestation numériquement, pointez votre arrivée le jour J via l'app, et votre facture est générée automatiquement à la fin.",
        color: "#F5A623",
    },
    {
        key: "paid",
        icon: "💶",
        title: "5. Vous êtes payé, on prend 8 %",
        subtitle: "Aucune déclaration sociale à faire",
        body: "Le client paie via Renforteo, vous êtes payé sous 72 heures. Nous prélevons 8 % de frais plateforme sur votre facture. Le reste est à vous. Le client paie également 15 % de frais plateforme.",
        color: "#10b981",
    },
];

const SCENE_MS = 6000;

export default function OnboardingVideo({ onClose }) {
    const [i, setI] = useState(0);
    const [playing, setPlaying] = useState(true);
    const [progress, setProgress] = useState(0);
    const startRef = useRef(Date.now());
    const scene = SCENES[i];

    useEffect(() => {
        if (!playing) return;
        startRef.current = Date.now();
        setProgress(0);
        const tick = setInterval(() => {
            const elapsed = Date.now() - startRef.current;
            const p = Math.min(elapsed / SCENE_MS, 1);
            setProgress(p);
            if (p >= 1) {
                if (i < SCENES.length - 1) setI(i + 1);
                else setPlaying(false);
            }
        }, 60);
        return () => clearInterval(tick);
    }, [i, playing]);

    const goto = (idx) => {
        setI(Math.max(0, Math.min(idx, SCENES.length - 1)));
        setPlaying(true);
    };

    const replay = () => {
        setI(0);
        setPlaying(true);
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
            data-testid="onboarding-video-modal"
        >
            <div
                className="relative w-full max-w-3xl aspect-video rounded-2xl overflow-hidden shadow-2xl transition-colors duration-700"
                style={{ background: `linear-gradient(135deg, ${scene.color}dd, ${scene.color}88)` }}
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur transition-colors"
                    data-testid="onboarding-video-close"
                    aria-label="Fermer"
                >
                    <X size={18} />
                </button>

                {/* Scene */}
                <div
                    key={scene.key}
                    className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 md:px-16 text-white"
                    style={{ animation: "fadeInUp 0.8s ease-out" }}
                >
                    <div
                        className="text-6xl md:text-8xl mb-4 md:mb-6"
                        style={{ animation: "popIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)" }}
                    >
                        {scene.icon}
                    </div>
                    <div className="uppercase tracking-widest text-[10px] md:text-xs text-white/70 mb-2">
                        Scène {i + 1} sur {SCENES.length}
                    </div>
                    <h2 className="font-display text-2xl md:text-4xl font-bold mb-2 md:mb-3 leading-tight">
                        {scene.title}
                    </h2>
                    <div className="text-sm md:text-base font-semibold text-white/90 mb-3 md:mb-5">
                        {scene.subtitle}
                    </div>
                    <p className="text-xs md:text-sm text-white/85 max-w-lg leading-relaxed">
                        {scene.body}
                    </p>
                </div>

                {/* Chapter dots */}
                <div className="absolute top-4 left-4 right-16 flex gap-1.5">
                    {SCENES.map((_, idx) => (
                        <button
                            key={idx}
                            onClick={() => goto(idx)}
                            className="flex-1 h-1 rounded-full bg-white/25 overflow-hidden hover:bg-white/35 transition-colors"
                            data-testid={`onboarding-video-dot-${idx}`}
                            aria-label={`Aller à la scène ${idx + 1}`}
                        >
                            <div
                                className="h-full bg-white transition-all duration-100"
                                style={{
                                    width:
                                        idx < i
                                            ? "100%"
                                            : idx === i
                                            ? `${progress * 100}%`
                                            : "0%",
                                }}
                            />
                        </button>
                    ))}
                </div>

                {/* Controls */}
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-center gap-2">
                    <button
                        onClick={() => goto(i - 1)}
                        disabled={i === 0}
                        className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur disabled:opacity-30 transition-colors"
                        data-testid="onboarding-video-prev"
                        aria-label="Précédent"
                    >
                        <SkipBack size={18} />
                    </button>
                    <button
                        onClick={() => setPlaying((p) => !p)}
                        className="p-3 rounded-full bg-white text-slate-900 hover:scale-110 transition-transform shadow-lg"
                        data-testid="onboarding-video-playpause"
                        aria-label={playing ? "Pause" : "Lecture"}
                    >
                        {playing ? <Pause size={22} /> : <Play size={22} />}
                    </button>
                    <button
                        onClick={() => (i < SCENES.length - 1 ? goto(i + 1) : replay())}
                        className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur transition-colors"
                        data-testid="onboarding-video-next"
                        aria-label="Suivant"
                    >
                        {i < SCENES.length - 1 ? <SkipForward size={18} /> : <RotateCcw size={18} />}
                    </button>
                </div>

                {/* Ambient particles */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-30">
                    <div className="particle" style={{ left: "10%", top: "20%", animationDelay: "0s" }} />
                    <div className="particle" style={{ left: "80%", top: "40%", animationDelay: "1.5s" }} />
                    <div className="particle" style={{ left: "60%", top: "70%", animationDelay: "3s" }} />
                    <div className="particle" style={{ left: "25%", top: "80%", animationDelay: "0.8s" }} />
                    <div className="particle" style={{ left: "90%", top: "15%", animationDelay: "2.2s" }} />
                </div>

                <style>{`
                    @keyframes fadeInUp {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes popIn {
                        0% { transform: scale(0) rotate(-10deg); opacity: 0; }
                        70% { transform: scale(1.15) rotate(3deg); }
                        100% { transform: scale(1) rotate(0); opacity: 1; }
                    }
                    @keyframes float {
                        0%, 100% { transform: translateY(0) scale(1); opacity: 0.4; }
                        50% { transform: translateY(-30px) scale(1.3); opacity: 0.8; }
                    }
                    .particle {
                        position: absolute;
                        width: 12px;
                        height: 12px;
                        background: rgba(255, 255, 255, 0.7);
                        border-radius: 50%;
                        animation: float 5s ease-in-out infinite;
                        filter: blur(1px);
                    }
                `}</style>
            </div>
        </div>
    );
}
